import Styled from 'styled-components'
import { State, useHookstate } from '@hookstate/core'
import { Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import { Module, ModulePerms, SubModule } from '../../domain/module'
import { getModulesUseCase } from '../../application/get-modules.use-case'
import { odinModuleRepository } from '../odin-module.repository'
import { useEffect } from 'react'
import { CollapsibleCardList } from '../../../../kernel/infrastructure/collapsible-card-list.component'
import { getIconAndTranslationByModuleId } from './roles-config.component'
import { getModulePermissionsUseCase } from '../../application/get-module-permissions.use-case'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { Can } from './access-control'
import React from 'react'

const Title = Styled.h2`
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  margin: 0;
  color: rgba(0, 0, 0, 0.85);
`
const listStyles: React.CSSProperties = {
  width: '100%',
  height: '57.7rem',
  paddingTop: '2rem',
  paddingBottom: '0.4rem',
  paddingRight: '2.4rem',
  margin: '0',
  overflowY: 'auto'
}
const headerStyles: React.CSSProperties = {
  width: '100%',
  height: '3.7rem',
  display: 'flex',
  justifyContent: 'space-between',
  paddingBottom: '1.2rem'
}
const bodyStyles: React.CSSProperties = {
  border: 'none',
  borderBottom: '1px solid #DDE0E5'
}
const HeaderContent = Styled.div<{ active: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  & > svg,
  & > span > svg {
    width: 1.6rem;
    height: 1.6rem;
    margin-right: 0.8rem;
    color: ${({ active }) => (active ? '#1890ff' : 'rgba(0, 0, 0, 0.25)')};
  }
  & > p {
    font-size: 16px;
    font-style: normal;
    font-weight: ${({ active }) => (active ? '700' : '300')};
    line-height: 24px;
    color: ${({ active }) => (active ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.45)')};
    margin: 0;
  }
`
const BodyContent = Styled.ul`
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0.4rem 0 0 2.4rem ;
  & > li{
    width: 100%;
    height: 2.4rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 1.2rem;
  }
`
const PermissionsIcon = Styled.span<{ available: boolean }>`
  display: flex;
  width: 24px;
  height: 24px;
  padding: 5.333px;
  justify-content: center;
  align-items: center;
  border-radius: 50px;
  background: ${({ available }) => (available ? '#E6F4FF' : '#FFF1F0')};
  color: ${({ available }) => (available ? '#0958D9' : '#A8071A')};
`

type RolePermissionsSumaryModalProps = {
  showModal: State<boolean, {}>
  user: any
}
const RolePermissionsSummaryModal = ({ showModal, user }: RolePermissionsSumaryModalProps) => {
  const { t } = useTranslation()
  const WIDTH_OF_MODAL_IN_PIXELS: number = 546
  const getModulesUseCaseInteractor = getModulesUseCase(odinModuleRepository)
  const getModulePermissionsUseCaseInteractor = getModulePermissionsUseCase(odinModuleRepository)
  const modules = useHookstate<Module[]>([])
  const subModules = useHookstate<SubModule[]>([])
  const selectedModule = useHookstate<Module>({} as Module)

  const getModules = async () => {
    let modulesResult: Module[] = await getModulesUseCaseInteractor()
    modulesResult = modulesResult.filter((module: Module) => Can([module.id]))
    modules.set(modulesResult)
  }
  const getModulePermissions = async (moduleId: string) => {
    const modulePermissionsResponse: SubModule[] = await getModulePermissionsUseCaseInteractor(moduleId)
    if (moduleId === ModulePerms.USE) {
      modulePermissionsResponse.forEach((subModule) => {
        if (subModule.id === 'USE_USER') {
          subModule.permissions = subModule.permissions.filter((permission) => !permission.id.includes('CLIENT'))
        }
      })
    }
    if (moduleId === ModulePerms.PAR) {
      let subModuleIndex = modulePermissionsResponse.findIndex((subModule) => subModule.id === 'PAR_GENERAL')
      modulePermissionsResponse.splice(subModuleIndex, 1)
    }
    subModules.set(modulePermissionsResponse)
  }

  const renderHeaderContent = (module: Module) => {
    const { icon, translation } = getIconAndTranslationByModuleId(module.id)
    return (
      <HeaderContent active={module.activeForClient}>
        {icon} <p>{t(translation)}</p>
      </HeaderContent>
    )
  }
  const renderBodyContent = () => {
    return (
      <BodyContent>
        {subModules.get().length > 0 ? (
          subModules.get().map((subModule) => {
            return (
              <React.Fragment key={subModule.id}>
                {subModule.permissions.map((permission) => (
                  <li key={permission.id}>
                    {t(`roles-permissions.permissions.${permission.id}.name`)}
                    {Can([permission.id]) ? (
                      <PermissionsIcon available={true}>
                        <CheckOutlined />
                      </PermissionsIcon>
                    ) : (
                      <PermissionsIcon available={false}>
                        <CloseOutlined />
                      </PermissionsIcon>
                    )}
                  </li>
                ))}
              </React.Fragment>
            )
          })
        ) : (
          <p>{t('roles-permissions.complete-access')}</p>
        )}
      </BodyContent>
    )
  }

  useEffect(() => {
    subModules.set([])
    if (selectedModule.get().id) {
      getModulePermissions(selectedModule.get().id)
    }
  }, [selectedModule])

  useEffect(() => {
    if (showModal.get()) getModules()
  }, [showModal])

  return (
    <Modal
      visible={showModal.get()}
      mask={true}
      onCancel={() => showModal.set(false)}
      maskStyle={{ zIndex: 10001000, background: 'rgba(0,0,0,0.45)' }}
      width={WIDTH_OF_MODAL_IN_PIXELS}
      footer={null}
      closable={true}
      centered
      bodyStyle={{ display: 'flex', flexDirection: 'column', padding: '1.6rem 0 1.6rem 2.4rem' }}
      destroyOnClose={true}
    >
      <Title>{`${t('roles-permissions.permissions-of')} ${user.avt_role?.name}`}</Title>
      <CollapsibleCardList
        arrowSize='large'
        arrowColorCB={(module: Module) => (module.activeForClient ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.45)')}
        arrowCursorCB={(module: Module) => (module.activeForClient ? 'pointer' : 'not-allowed')}
        headerContent={renderHeaderContent}
        bodyContent={renderBodyContent}
        dataArray={[...modules.get()]}
        listStyle={listStyles}
        headerStyle={headerStyles}
        bodyStyle={bodyStyles}
        selectionCallback={(module: Module) => selectedModule.set(JSON.parse(JSON.stringify(module)))}
        disabledCallback={(module: Module) => !module.activeForClient}
      />
    </Modal>
  )
}

export { RolePermissionsSummaryModal }
